import React, { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../AppContextDef";
import { MeetingProvider } from "@videosdk.live/react-sdk";
import imageSrc from "../logo.png";
import useIsMobile from "../utils/useIsMobile";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import useTimer from "../utils/useTimer";
import ConfirmBox from "../components/ConfirmBox";
import RecordingContainer from "../components/pubsubDataContainer/RecordingContainer";
import "../i18n";
import { fontMap } from "../utils/common";

const RecordingPage = ({ formattedTime }) => {
  const {
    currentPageData: currentPage,
    isDisagreeButtonClick,
    selectedLang,
    applicationNo,
    allPubsubData,
  } = useAppContext();

  const searchParams = new URLSearchParams(
    "?" + window.location.hash.split("?")[1]
  );
  let meetingId = searchParams.get("id");
  let token = searchParams.get("token");
  const searchParticipantParams = new URLSearchParams(window.location.search);
  let participantId = searchParticipantParams.get("participantId");
  const [roomId, setRoomId] = useState(null);
  const [meetingIdErrorVisible, setMeetingIdErrorVisible] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [errCode, setErrCode] = useState(null);
  const isMobile = useIsMobile();

  const validateMeetingId = async ({ meetingId, token }) => {
    const BASE_URL = process.env.REACT_APP_VIDEOSDK_URL;

    const urlMeetingId = `${BASE_URL}/v1/prebuilt/meetings/${meetingId}`;

    const resMeetingId = await fetch(urlMeetingId, {
      method: "POST",
      headers: { "Content-type": "application/json", Authorization: token },
    });

    const meetingIdJson = await resMeetingId.json();

    const validatedMeetingId = meetingIdJson.meetingId;

    if (validatedMeetingId) {
      setRoomId(validatedMeetingId);
    } else {
      setMeetingIdErrorVisible(true);
      setErrCode(meetingIdJson.statusCode);
      setErrMsg(
        meetingIdJson.error ? meetingIdJson.error : "Unable to join meeting!"
      );
    }
  };

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLang?.lang);
  }, [selectedLang]);

  const steps = [
    {
      title: "Start Your Journey",
      value: "Intro",
    },
    {
      title: "Personal Details",
      value: "PersonalDetails",
    },
    {
      title: "Policy Details",
      value: "PolicyDetails",
    },
    {
      title: "Video Consent",
      value: "VideoConsent",
    },
  ];

  const currentStepIndex = steps.findIndex(
    (step) => step.value === currentPage
  );

  const isTimerActive = useMemo(() => {
    return (
      currentPage !== "Intro" &&
      currentPage !== "Thankyou" &&
      currentPage !== "Disagree" &&
      currentPage !== "Recording" &&
      currentPage !== null
    );
  }, [currentPage]);
  const { resetTimer, startTimer } = useTimer();

  useEffect(() => {
    if (!isTimerActive) {
      resetTimer();
    } else {
      startTimer();
    }
  }, [isTimerActive]);

  useEffect(() => {
    if (currentPage === "  ") {
      resetTimer();
    }
  }, [currentPage]);

  const ValidateMeetingCall = async () => {
    await validateMeetingId({ meetingId: meetingId, token: token });
  };

  useEffect(() => {
    if (meetingId && token) {
      ValidateMeetingCall();
    }
  }, [meetingId, token]);

  const fontFamilyCSS = fontMap[selectedLang?.lang] || "Lato";

  return (
    <div className="flex flex-1">
      <div
        style={{ width: !isMobile && 325, fontFamily: fontFamilyCSS }}
        className={`bg-white md:bg-[#F3F7FD] md:px-10 pt-4 md:pb-0 shadow-lg flex h-full flex-col sticky top-0 z-40 `}
      >
        <>
          <div className="px-3 md:px-0">
            <img src={imageSrc} alt="logo" width={isMobile && 200} />
          </div>
          <div className="mt-3">
            <p className="text-sm lg:text-base font-bold text-black-150">
              {`${t(`Application number`)}: ${
                applicationNo ? applicationNo : allPubsubData?.applicationNo
              }`}
            </p>
          </div>
          <div className="flex flex-1 md:flex-col pt-5 px-3 gap-2 pb-4 md:pb-0 md:px-0 md:pt-0 overflow-x-auto md:overflow-x-visible md:justify-center">
            {steps.map(({ title, value }, index) => {
              const isCompleted = index < currentStepIndex; // Step is completed
              const isCurrent = value === currentPage; // Current step
              return (
                <div
                  key={index}
                  className={`${index !== 0 && "md:mt-4"} ${
                    isCurrent
                      ? "bg-blue-150 border-blue-150 md:bg-transparent"
                      : isMobile && "border border-gray-275 md:border-hidden"
                  } rounded-md md:rounded-none flex items-center px-2 py-[6px] md:p-0 `}
                >
                  {isCompleted ? (
                    <div className="hidden md:block">
                      <CheckCircleIcon className="w-5 h-5 text-green-150" />{" "}
                    </div>
                  ) : (
                    <div
                      className={`hidden md:block w-4 h-4 rounded-full ${
                        isCurrent
                          ? "border-black-150 border "
                          : "border-gray-150 border "
                      }
                }`}
                    ></div>
                  )}
                  <p
                    className={`${
                      isCompleted ? "md:ml-3" : "md:ml-4"
                    }  text-sm md:text-base font-bold whitespace-nowrap truncate ${
                      isCurrent
                        ? isMobile
                          ? "text-white"
                          : "text-black"
                        : isCompleted && isMobile
                        ? "text-black-150"
                        : "text-gray-150"
                    } mb-0`}
                  >
                    {t(`${title}`)}
                  </p>
                </div>
              );
            })}
          </div>
        </>
      </div>
      {roomId && (
        <MeetingProvider
          config={{
            meetingId: roomId,
            micEnabled: false,
            webcamEnabled: false,
            name: "Recorder",
            signalingBaseUrl: "api.videosdk.live",
            participantId,
          }}
          token={token}
          joinWithoutUserInteraction={true}
        >
          <RecordingContainer
            formattedTime={formattedTime}
            currentPage={currentPage}
            isDisagreeButtonClick={isDisagreeButtonClick}
          />
        </MeetingProvider>
      )}
      <ConfirmBox
        open={meetingIdErrorVisible}
        setOpen={setMeetingIdErrorVisible}
        extraText={`Error Code : ${errCode}`}
        text={errMsg}
        buttonText={"Close"}
        buttonHandler={() => {
          setMeetingIdErrorVisible(false);
        }}
      />
    </div>
  );
};

export default RecordingPage;
